import {
  arrow,
  autoUpdate,
  useFloating,
  offset,
  flip,
  shift,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useClick,
} from '@floating-ui/react';
import React, { useState } from 'react';
import { ToggletipOptions } from '../../Toggletip';

export function useToggletip({
  placement = 'top',
  variant = 'light',
}: ToggletipOptions = {}) {
  const arrowRef = React.useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const open = isOpen;

  const data = useFloating({
    placement,
    open: isOpen,
    onOpenChange: (isOpen) => {
      setIsOpen(isOpen);
    },
    // required to prevent loop with other usage of ResizeObserver https://floating-ui.com/docs/autoupdate#elementresize
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        elementResize: false,
      });
      return cleanup;
    },
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;
  const click = useClick(context);
  const focus = useFocus(context, {
    enabled: true,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([click, focus, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setIsOpen,
      arrowRef,
      variant,
      ...interactions,
      ...data,
    }),
    [open, setIsOpen, variant, interactions, data]
  );
}
